import EmberObject from '@ember/object';
import Service from '@ember/service';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import config from '@linc-technologies/linced-common/utils/config';

export default class HeroApiGoalsService extends Service {
  @service
  fetch;

  /**
   * getLearnerSummary returns the requested learner's goal summary.
   *
   * @param {String} schoolId - The school to obtain goal summaries from.
   * @param {String} personId - The person to get a goal summary for.
   * @param {String} subjectId - The subject to get a goal summary for.
   * @param {Number} [asAtTimeMillis] - Timestamp to return a summary "as at".
   */
  getLearnerSummary(schoolId, personId, subjectId, asAtTimeMillis) {
    let uri = `${config.APP.apiHost}/goals/v4/learnersummary?personId=${personId}&schoolId=${schoolId}&subjectId=${subjectId}`;
    if (!isEmpty(asAtTimeMillis)) {
      uri = `${uri}&asAtTimeMillis=${asAtTimeMillis}`;
    }

    return this.fetch.request(`${uri}`).then(normaliseLearnerSummary);
  }

  /**
   * listLearnerSummaries returns the requested learners' goal summaries.
   * Either groupId or personIds must be provided.
   *
   * @param {String} schoolId - The school to obtain goal summaries from.
   * @param {String} groupId - The group to get goal summaries for.
   * @param {Array<String>} personIds - The people to get goal summaries for.
   * @param {String} subjectId - The subject to get a goal summary for.
   * @param {Number} [asAtTimeMillis] - Timestamp to return a summary "as at".
   */
  listLearnerSummaries(
    schoolId,
    groupId,
    personIds,
    subjectId,
    asAtTimeMillis,
  ) {
    let params = new URLSearchParams({
      schoolId,
      subjectId,
    });
    if (!isEmpty(groupId)) {
      params.append('groupId', groupId);
    }
    if (!isEmpty(personIds)) {
      params.append('personIds', JSON.stringify(personIds));
    }
    if (!isEmpty(asAtTimeMillis)) {
      params.append('asAtTimeMillis', asAtTimeMillis);
    }
    let query = params.toString();
    let uri = `${config.APP.apiHost}/goals/v4/learnersummaries?${query}`;

    return this.fetch.request(`${uri}`).then(normaliseLearnerSummaries);
  }
}

// schemaLearnerSummary returns the defaults for the API model.
function schemaLearnerSummary() {
  return {
    // personId is the unique identifier of the Person (Learner) who
    // this summary is for.
    personId: '',
    // schoolId is the unique identifier of the School to which this
    // summary relates.
    schoolId: '',
    // campusId is the unique identifier of the Campus to which this
    // summary relates.
    campusId: '',
    // subjectId is the unique identifier of the Goal Subject to
    // which this summary relates (if applicable).
    subjectId: '',
    // isPLP denotes whether this summary is for a Personal Learning
    // Plan.
    isPLP: '',
    // categories is a list of category-level summaries.
    categories: [],
  };
}

// schemaLearnerCategorySummary returns the defaults for the API model.
function schemaLearnerCategorySummary() {
  return {
    // categoryId is the unique identifier of the Goal Category to
    // which this summary relates.
    categoryId: '',
    // totalGoals is the total number of Goals within this Category.
    totalGoals: 0,
    // notSet is the number of Goals within this Category which the
    // Learner has not completed and is not currently working towards.
    notSet: 0,
    // developing is the number of Goals within this Category which
    // the Learner is currently working towards.
    developing: 0,
    // secure is the number of Goals within this Category which the
    // Learner has already completed.
    secure: 0,
  };
}

/**
 * newApiObject returns an object with all data populated with defaults.
 *
 * @param {Object} schema - a given schema.
 * @param {Object} data - the data to populate into the schema.
 * @returns {Object}
 */
function newApiObject(schema, data) {
  let obj = EmberObject.create(schema);
  if (!isEmpty(data)) {
    obj.setProperties(data);
  }

  return obj;
}

function normaliseLearnerSummaries(response) {
  let method = 'normaliseLearnerSummaries';

  if (!!response && !!response.summaries) {
    let summaries = response.summaries.map((summary) =>
      normaliseLearnerSummaryCategories(summary),
    );

    return Promise.resolve(summaries, method);
  }

  return Promise.resolve(null, method);
}

function normaliseLearnerSummary(response) {
  let method = 'normaliseLearnerSummary';

  if (!!response && !!response.summary) {
    let learnerSummary = normaliseLearnerSummaryCategories(response.summary);

    return Promise.resolve(learnerSummary, method);
  }

  return Promise.resolve(null, method);
}

function normaliseLearnerSummaryCategories(summary) {
  let learnerSummary = newApiObject(schemaLearnerSummary(), summary);

  if (!isEmpty(summary.categories)) {
    learnerSummary.categories = [];
    for (let i = 0; i < summary.categories.length; i++) {
      let schema = schemaLearnerCategorySummary();
      let data = normaliseLearnerSummaryCategory(summary.categories[i]);
      let category = newApiObject(schema, data);

      learnerSummary.categories.push(category);
    }
  }

  return learnerSummary;
}

function normaliseLearnerSummaryCategory(category) {
  // NB. I tried making this an A() and .includes didn't work properly :|
  let numericProperties = ['totalGoals', 'notSet', 'developing', 'secure'];
  Object.keys(category).forEach((property) => {
    if (numericProperties.includes(property)) {
      category[property] = parseInt(category[property], 10);
    }
  });

  return category;
}
